<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue';
import { i18n } from '@/i18n';
import TextButton from '@/components/input/Btn/TextButton.vue';

const t = i18n.t;

const isSelectedRegisterBusiness = ref(true);

const registerBusinessStyle = computed((): string => {
  return isSelectedRegisterBusiness.value ? 'first-register-container--selected' : 'first-register-container--not-selected';
});

const registerInputStyle = computed((): string => {
  return isSelectedRegisterBusiness.value ? 'first-register-container--not-selected' : 'first-register-container--selected';
});

const props = withDefaults(
  defineProps<{
    propString?: string;
  }>(),
  {
    propString: 'default string',
  }
);

const emits = defineEmits<{
  (e: 'on-click-next', isSelectedRegisterBusiness: boolean): void;
  (e: 'on-click-back'): void;
  (e: 'on-click-download-template'): void;
  (e: 'on-click-register-setting'): void;
}>();

/**
 * 戻るボタン（STOPSHIP: ここ、何するボタン？？？　とりあえずおいてるだけ）
 */
const onClickBackButton = () => {
  emits('on-click-back');
};

/**
 * 次へボタン
 */
const onClickNextButton = () => {
  emits('on-click-next', isSelectedRegisterBusiness.value);
};

/**
 * テンプレートダウンロードボタン
 */
const onClickDownloadTemplate = () => {
  emits('on-click-download-template');
};

/**
 * 登録設定ボタン
 */
const onClickRegisterSetting = () => {
  emits('on-click-register-setting');
};
</script>

<template>
  <div class="first-register">
    <div class="first-register__container first-register-container">
      <label
        for="first-register-container-business-button"
        class="first-register-container__business first-register-container-business"
        :class="registerBusinessStyle"
      >
        <div class="first-register-container-business__info register-business-info">
          <i18n-t class="register-business-info__title i18n-break" :keypath="'取引設定：タイトル'" tag="span">
            <template #newLine>
              <br />
            </template>
          </i18n-t>
          <i18n-t class="register-business-info__description i18n-break" :keypath="'取引設定：本文'" tag="span">
            <template #newLine>
              <br />
            </template>
          </i18n-t>
          <div class="register-business-info__link-text" @click="onClickDownloadTemplate">
            <i18n-t class="i18n-break" :keypath="'取引設定：リンクテキスト'" tag="span">
              <template #newLine>
                <br />
              </template>
            </i18n-t>
          </div>

          <i18n-t class="register-business-info__usage i18n-break" :keypath="'取引設定：使用例'" tag="span">
            <template #newLine>
              <br />
            </template>
          </i18n-t>
        </div>
        <input
          id="first-register-container-business-button"
          v-model="isSelectedRegisterBusiness"
          class="first-register-container-business__button"
          type="radio"
          :value="true"
        />
      </label>

      <label
        for="first-register-container-input-button"
        class="first-register-container__input first-register-container-input"
        :class="registerInputStyle"
      >
        <div class="first-register-container-input__info register-input-info">
          <i18n-t class="register-input-info__title i18n-break" :keypath="'数値設定：タイトル'" tag="span">
            <template #newLine>
              <br />
            </template>
          </i18n-t>
          <i18n-t class="register-input-info__description i18n-break" :keypath="'数値設定：本文'" tag="span">
            <template #newLine>
              <br />
            </template>
          </i18n-t>
          <div class="register-input-info__link-text" @click="onClickRegisterSetting">
            <i18n-t class="i18n-break" :keypath="'数値設定：リンクテキスト'" tag="span">
              <template #newLine>
                <br />
              </template>
            </i18n-t>
          </div>

          <i18n-t class="register-input-info__usage i18n-break" :keypath="'数値設定：使用例'" tag="span">
            <template #newLine>
              <br />
            </template>
          </i18n-t>
        </div>
        <input
          id="first-register-container-input-button"
          v-model="isSelectedRegisterBusiness"
          class="first-register-container-input__button"
          type="radio"
          :value="false"
        />
      </label>
    </div>

    <div class="first-register__button-container register-button-container">
      <TextButton
        :button-style="'btn-secondary'"
        :size="'btn-md'"
        :on-click="onClickBackButton"
        :disabled="true"
        class="bulk-action-button-container__export-button"
      >
        {{ t('初期設定：戻るボタン') }}
      </TextButton>
      <TextButton
        :button-style="'btn-primary'"
        :size="'btn-md'"
        :on-click="onClickNextButton"
        :disabled="false"
        class="bulk-action-button-container__export-button"
      >
        {{ t('初期設定：次へボタン') }}
      </TextButton>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.first-register {
  display: grid;
  gap: 12px;
}

.first-register-container {
  display: grid;
  gap: 8px;
  margin-top: 16px;
  grid-template-columns: auto auto;

  &--selected {
    background-color: $semanticPrimarySubdued;
  }

  &--not-selected {
    background-color: $semanticWhite;
  }
}

.first-register-container-business {
  display: grid;
  gap: 8px;
  padding: 20px 16px 48px 16px;
  border: 1px solid $semanticPrimary;
  grid-template-columns: 1fr fit-content(100%);

  &__button {
    height: 36px;
    width: 36px;
  }
}

.register-business-info {
  display: grid;
  gap: 8px;

  &__title {
    font-size: 22px;
    font-weight: 700;
    line-height: 28px;
    color: $textDefault;
    margin-bottom: 28px;
  }

  &__description {
    font-size: 16px;
    font-weight: 400;
    line-height: 20px;
    color: $textSecondary;
  }

  &__link-text {
    font-size: 16px;
    font-weight: 700;
    line-height: 20px;
    color: $semanticPrimary;
    margin-bottom: 48px;
    cursor: pointer;
  }

  &__usage {
    font-size: 16px;
    font-weight: 400;
    line-height: 20px;
    margin-left: 80px;
    color: $textSecondary;
  }
}

.first-register-container-input {
  display: grid;
  gap: 8px;
  padding: 20px 16px 48px 16px;
  border: 1px solid $semanticPrimary;
  grid-template-columns: 1fr fit-content(100%);

  &__button {
    height: 36px;
    width: 36px;
  }
}

.register-input-info {
  display: grid;
  gap: 8px;

  &__title {
    font-size: 22px;
    font-weight: 700;
    line-height: 28px;
    color: $textDefault;
    margin-bottom: 28px;
  }

  &__description {
    font-size: 16px;
    font-weight: 400;
    line-height: 20px;
    color: $textSecondary;
  }

  &__link-text {
    font-size: 16px;
    font-weight: 700;
    line-height: 20px;
    color: $semanticPrimary;
    margin-bottom: 48px;
    cursor: pointer;
  }

  &__usage {
    font-size: 16px;
    font-weight: 400;
    line-height: 20px;
    margin-left: 80px;
    color: $textSecondary;
  }
}

.register-button-container {
  display: grid;
  gap: 8px;
  grid-template-columns: repeat(2, fit-content(100%));
}
</style>
